import { useState } from 'react';
import {
  DialogContent,
  Typography,
  Button,
  useMediaQuery,
  Box,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import LoadingLogoSkeleton from '../../../components/SkeletonLoading/loadingLogoSkeleton';
import { addSurvey, addSurveyQuestion } from '../../../services/survey_services';
import { slideUpdateCompany } from '../../../services/company_services';
import eventBus from '../../../eventBus';

const useStyles = ({ isSM }) => ({
  dialogContent: {
    padding: isSM ? '20px' : '16px 0px',
  },
});

export default function Update({
  updateType,
  setBlockClosePopup,
  handleClose,
  setIsSnackbarVisible,
}) {
  const { accountDetails, asLoggedUserDetails } = useAccount();
  const [loading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles({ isSM });

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value.length > 0 && value.length < 30) {
      setError('The input must be at least 30 characters long.');
    } else {
      setError('');
    }
  };

  async function updateCurrentGoal() {
    setIsLoading(true);
    setBlockClosePopup(true);
    try {
      let data = {
        CONTACT_ID: asLoggedUserDetails.userId,
        DISPLAY_NAME: accountDetails.displayName || '',
        COMPANY_NAME: accountDetails.displayName || '',
        COMPANY_STORY: accountDetails.storyAndMission || '',
        COMPANY_INDUSTRY: accountDetails.industryTagIds,
        COMPANY_DESIRED_INDUSTRY: accountDetails.desiredIndustriesTagIds,
        COMPANY_SKILLS: accountDetails.desiredSkillsTagIds,
        COMPANY_BENEFITS: accountDetails.benefits
          ? accountDetails.benefits.map((e) => JSON.stringify(e))
          : [],
        COMPANY_WEBSITE: accountDetails.website || '',
        COMPANY_SIZE: accountDetails.companySize || '',
        CURRENT_GOAL: inputValue || '',
      };
      await slideUpdateCompany(data);

      editAccountInformation({
        currentGoal: inputValue,
      });
      handleClose();
      setInputValue('');
    } catch (error) {
      console.error('Error updating goal:', error);
      setIsSnackbarVisible(true);
    } finally {
      setIsLoading(false);
      setBlockClosePopup(false);
    }
  }

  const updateSurvey = async () => {
    setIsLoading(true);
    setBlockClosePopup(true);
    try {
      const survey = await addSurvey({
        COMPANY_ID: accountDetails.id,
        ADMIN_ID: '',
      });
      await addSurveyQuestion({
        COMPANY_ID: accountDetails?.id,
        SURVEY_ID: survey.data.addSurvey.id,
        BODY: inputValue,
      });
      handleClose();
      setInputValue('');
      eventBus.dispatch('surveyUpdated', {question: inputValue});
    } catch (error) {
      console.error('Error adding survey question:', error);
      setIsSnackbarVisible(true);
    } finally {
      setIsLoading(false);
      setBlockClosePopup(false);
    }
  };

  return (
    <DialogContent sx={classes.dialogContent}>
      <Box sx={{ position: 'relative', padding: '20px' }}>
        <DialogTitle>
          <Typography
            sx={{ fontSize: '20px', fontWeight: '600', lineHeight: '26px' }}
          >
            {updateType === 'goal'
              ? 'Update your current challenge or goal'
              : 'Update your survey'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            multiline
            rows={7}
            fullWidth
            variant="outlined"
            value={inputValue}
            onChange={handleInputChange}
            inputProps={{ maxLength: 500 }}
            sx={{
              mt: 0,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '14px',
                color: '#333',
                height: '176px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#ddd',
              },
            }}
          />
          <Box
            sx={{
              height: '20px',
              mt: 1,
            }}
          >
            {error && (
              <Typography
                sx={{
                  color: 'red',
                  fontSize: '12px',
                }}
              >
                {error}
              </Typography>
            )}
          </Box>
          <Button
            variant="contained"
            onClick={updateType === 'goal' ? updateCurrentGoal : updateSurvey}
            fullWidth
            disabled={loading || inputValue.length < 30}
            sx={{
              textTransform: 'none',
              bgcolor: loading ? '#cccccc' : '#2B71F6',
              borderRadius: '8px',
              boxShadow: 'none',
              width: '100%',
              height: 42,
              fontSize: 14,
              fontWeight: '600',
              mt: 1,
              '&:hover': {
                bgcolor: loading ? '#cccccc' : '#2B71F6',
              },
            }}
          >
            {loading ? <LoadingLogoSkeleton /> : 'Save'}
          </Button>
        </DialogContent>
      </Box>
    </DialogContent>
  );
}
